import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiltersBar, IconButton, Page, SingleSelectFilter } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { PageLoader } from '~/routes/public/pages';
import WorkspaceMembersModal from '../WorkspaceMembersModal';
import ProjectMembersList from './ProjectMembersList';

export default function ProjectMembersTab({ project }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();

  const [isActive, setIsActive] = useState('true');
  const [members, setMembers] = useState(null);
  const [dialog, setDialog] = useState(null);

  // Read unfiltered and filter by status in the browser, so the modal still sees members the filter hides.
  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).members().get({ size: 1000 });
    setMembers(data);
  }, [api, workspace.id, project.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const data = useMemo(() => {
    if (!members) return null;
    if (isActive === 'true') return members.filter((projectMember) => projectMember.isActuallyActive);
    if (isActive === 'false') return members.filter((projectMember) => !projectMember.isActuallyActive);
    return members;
  }, [members, isActive]);

  const addMembers = async (membersToAdd, failureMessage) => {
    if (membersToAdd.length === 0) return;

    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projectSettings(project.id)
        .members()
        .batch.add(membersToAdd.map((member) => ({ memberId: member.id })));

      const { succeeded } = data.summary;
      if (succeeded === data.rows.length) {
        toast.success('Members added successfully.');
      } else if (succeeded > 0) {
        toast.error(`Only ${succeeded} of the ${data.rows.length} selected members could be added.`);
      } else {
        toast.error(failureMessage);
      }
      await fetchData();
    } catch {
      toast.error(failureMessage);
    }
  };

  const handleAddMembers = () => {
    setDialog(
      <WorkspaceMembersModal
        projectId={project.id}
        projectMembers={(members ?? []).map((projectMember) => ({
          memberId: projectMember.member.id,
          isActive: projectMember.isActive,
        }))}
        onChange={(selectedMembers) => addMembers(selectedMembers, 'Failed to add the selected members.')}
        onClose={() => setDialog(null)}
      />,
    );
  };

  if (!data) return <PageLoader />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Team</Page.Title>

        <Page.Actions>
          {project.permissions.edit && <IconButton icon="plus" tooltip="Add Members" onClick={handleAddMembers} />}
        </Page.Actions>
      </Page.TabHeader>

      <Page.Filters>
        <FiltersBar>
          <SingleSelectFilter
            name="isActive"
            icon="filter"
            placeholder="Member Status"
            options={[
              { id: 'true', name: 'Active' },
              { id: 'false', name: 'Inactive' },
            ]}
            value={isActive}
            onChange={({ target: { value } }) => setIsActive(value)}
          />
        </FiltersBar>
      </Page.Filters>

      <ProjectMembersList project={project} data={data} members={members} onRefetch={fetchData} />

      {dialog}
    </>
  );
}
